/**
 * BlockPalette
 *
 * Sidebar panel showing all registered blocks grouped by category.
 * Used in the page builder editor to add blocks to the canvas.
 *
 * Enhanced:
 * - Colored category badges with Lucide icons
 * - maxPerPage enforcement with "Max reached" badge
 * - Recently used blocks (localStorage)
 * - Better item layout with description truncation
 * - Hover "+" add button
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, ChevronDown, ChevronRight, Plus,
  Layout, FileText, BarChart3, Image as ImageIcon,
  DollarSign, HelpCircle, Megaphone, Puzzle, Clock,
} from 'lucide-react';
import { Input } from '../ui/input';
import { getAllBlocks } from './componentRegistry';
import type { BlockRegistryEntry, BlockCategory, BlockInstance } from './types';

/* ------------------------------------------------------------------ */
/*  Category metadata                                                  */
/* ------------------------------------------------------------------ */
const CATEGORY_ORDER: BlockCategory[] = [
  'layout',
  'content',
  'data',
  'media',
  'investment',
  'interactive',
  'marketing',
  'custom',
];

const CATEGORY_LABELS: Record<string, string> = {
  layout: 'Layout',
  content: 'Content',
  data: 'Data & Metrics',
  media: 'Media',
  investment: 'Investment',
  interactive: 'Interactive',
  marketing: 'Marketing & Site',
  custom: 'Custom',
};

const CATEGORY_COLORS: Record<string, string> = {
  layout: 'bg-purple-100 text-purple-700',
  content: 'bg-blue-100 text-blue-700',
  data: 'bg-green-100 text-green-700',
  media: 'bg-orange-100 text-orange-700',
  investment: 'bg-yellow-100 text-yellow-700',
  interactive: 'bg-pink-100 text-pink-700',
  marketing: 'bg-indigo-100 text-indigo-700',
  custom: 'bg-gray-100 text-gray-700',
};

const CATEGORY_BAR_COLORS: Record<string, string> = {
  layout: 'bg-purple-400',
  content: 'bg-blue-400',
  data: 'bg-green-400',
  media: 'bg-orange-400',
  investment: 'bg-yellow-400',
  interactive: 'bg-pink-400',
  marketing: 'bg-indigo-400',
  custom: 'bg-gray-400',
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  layout: <Layout className="h-3 w-3" />,
  content: <FileText className="h-3 w-3" />,
  data: <BarChart3 className="h-3 w-3" />,
  media: <ImageIcon className="h-3 w-3" />,
  investment: <DollarSign className="h-3 w-3" />,
  interactive: <HelpCircle className="h-3 w-3" />,
  marketing: <Megaphone className="h-3 w-3" />,
  custom: <Puzzle className="h-3 w-3" />,
};

/* ------------------------------------------------------------------ */
/*  localStorage helpers                                               */
/* ------------------------------------------------------------------ */
const RECENT_KEY = 'pagebuilder_recent_blocks';
const MAX_RECENT = 5;

function getRecentBlocks(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function pushRecentBlock(blockKey: string): void {
  try {
    const current = getRecentBlocks();
    const updated = [blockKey, ...current.filter((k) => k !== blockKey)].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
  } catch {
    // ignore
  }
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */
export interface BlockPaletteProps {
  onAddBlock: (blockType: string) => void;
  /** Extra block entries not in the global registry (e.g. custom blocks) */
  extraBlocks?: BlockRegistryEntry[];
  /** Current page blocks — used for maxPerPage enforcement */
  existingBlocks?: BlockInstance[];
}

/* ------------------------------------------------------------------ */
/*  Block item component (card style)                                 */
/* ------------------------------------------------------------------ */
function BlockItem({
  entry,
  onAdd,
  disabled,
  maxReached,
}: {
  entry: BlockRegistryEntry;
  onAdd: () => void;
  disabled: boolean;
  maxReached: boolean;
}) {
  const barColor = CATEGORY_BAR_COLORS[entry.category] || CATEGORY_BAR_COLORS.custom;

  return (
    <button
      onClick={disabled ? undefined : onAdd}
      disabled={disabled}
      className={`relative flex flex-col text-left border rounded-md overflow-hidden min-h-[68px] transition-all ${
        disabled
          ? 'opacity-50 cursor-not-allowed border-border bg-background'
          : 'border-border bg-background hover:border-primary hover:shadow-sm cursor-pointer group'
      }`}
    >
      {/* Category color bar */}
      <div className={`h-1 w-full shrink-0 ${barColor}`} />

      {/* Card content */}
      <div className="flex-1 p-2">
        <div className="flex items-start justify-between gap-1">
          <p className="text-xs font-medium leading-tight">{entry.label}</p>
          {maxReached && (
            <span className="text-[9px] font-medium bg-muted text-muted-foreground rounded px-1 py-0.5 shrink-0">Max</span>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground leading-tight mt-0.5 line-clamp-2">
          {entry.description}
        </p>
      </div>

      {/* Add overlay on hover */}
      {!disabled && (
        <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center">
            <Plus className="h-3.5 w-3.5" />
          </div>
        </div>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function BlockPalette({
  onAddBlock,
  extraBlocks = [],
  existingBlocks = [],
}: BlockPaletteProps) {
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [recentKeys, setRecentKeys] = useState<string[]>([]);

  // Load recent blocks on mount
  useEffect(() => {
    setRecentKeys(getRecentBlocks());
  }, []);

  const allBlocks: BlockRegistryEntry[] = [...getAllBlocks(), ...extraBlocks];

  const handleAdd = useCallback(
    (blockKey: string) => {
      pushRecentBlock(blockKey);
      setRecentKeys(getRecentBlocks());
      onAddBlock(blockKey);
    },
    [onAddBlock]
  );

  const isMaxReached = (entry: BlockRegistryEntry): boolean => {
    if (entry.maxPerPage === undefined) return false;
    return existingBlocks.filter((b) => b.blockKey === entry.key).length >= entry.maxPerPage;
  };

  const filtered = query.trim()
    ? allBlocks.filter(
        (b) =>
          b.label.toLowerCase().includes(query.toLowerCase()) ||
          b.description.toLowerCase().includes(query.toLowerCase())
      )
    : allBlocks;

  // Group by category, preserving CATEGORY_ORDER
  const grouped: Record<string, BlockRegistryEntry[]> = {};
  for (const b of filtered) {
    if (!grouped[b.category]) grouped[b.category] = [];
    grouped[b.category].push(b);
  }

  const categories = CATEGORY_ORDER.filter((c) => grouped[c]?.length);
  for (const cat of Object.keys(grouped)) {
    if (!categories.includes(cat as BlockCategory)) {
      categories.push(cat as BlockCategory);
    }
  }

  const toggleCat = (cat: string) => {
    setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Recent blocks (resolved to entries)
  const recentEntries: BlockRegistryEntry[] = recentKeys
    .map((key) => allBlocks.find((b) => b.key === key))
    .filter((e): e is BlockRegistryEntry => !!e)
    .slice(0, MAX_RECENT);

  const showRecent = recentEntries.length > 0 && !query.trim();

  return (
    <div className="flex flex-col h-full bg-white border-r border-border">
      {/* Header */}
      <div className="px-3 py-3 border-b border-border">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          Blocks
        </p>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search blocks…"
            className="pl-7 h-8 text-xs"
          />
        </div>
      </div>

      {/* Block list */}
      <div className="flex-1 overflow-y-auto">
        {categories.length === 0 && (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">
            No blocks match "{query}"
          </p>
        )}

        {/* Recently used section */}
        {showRecent && (
          <div className="border-b border-border/50">
            <button
              onClick={() => toggleCat('__recent')}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/30 transition-colors"
            >
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                Recent
              </span>
              {collapsed['__recent'] ? (
                <ChevronRight size={12} className="text-muted-foreground" />
              ) : (
                <ChevronDown size={12} className="text-muted-foreground" />
              )}
            </button>
            {!collapsed['__recent'] && (
              <div className="grid grid-cols-2 gap-1.5 p-2">
                {recentEntries.map((entry) => (
                  <BlockItem
                    key={entry.key}
                    entry={entry}
                    onAdd={() => handleAdd(entry.key)}
                    disabled={isMaxReached(entry)}
                    maxReached={isMaxReached(entry)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {categories.map((cat) => {
          const blocks = grouped[cat] ?? [];
          const isOpen = !collapsed[cat];

          return (
            <div key={cat} className="border-b border-border/50">
              {/* Category header */}
              <button
                onClick={() => toggleCat(cat)}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/30 transition-colors"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {CATEGORY_LABELS[cat] || cat}
                </span>
                {isOpen ? (
                  <ChevronDown size={12} className="text-muted-foreground" />
                ) : (
                  <ChevronRight size={12} className="text-muted-foreground" />
                )}
              </button>

              {/* Block items */}
              {isOpen && (
                <div className="grid grid-cols-2 gap-1.5 p-2">
                  {blocks.map((entry) => (
                    <BlockItem
                      key={entry.key}
                      entry={entry}
                      onAdd={() => handleAdd(entry.key)}
                      disabled={isMaxReached(entry)}
                      maxReached={isMaxReached(entry)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
